/* eslint-disable no-nested-ternary */
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// config
import { USER_PATH_AFTER_LOGIN } from 'src/config-global';
// routes
// import { useSearchParams } from 'src/routes/hook';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import { setSession } from './utils';
// hooks
import { useOtpSession } from '../hooks/use-otp-session';
import { useRefCode } from '../hooks/use-ref-code';
// types
import { ActionMapType, AuthStateType, AuthUserType } from '../types';
//
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  UPDATE = 'UPDATE',
  UPDATE_TOKENS = 'UPDATE_TOKENS',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.UPDATE]: {
    user: AuthUserType;
  };
  [Types.UPDATE_TOKENS]: {
    tokens: number;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.UPDATE) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.UPDATE_TOKENS) {
    if (!state.user) return state;

    return {
      ...state,
      user: {
        ...state.user,
        tokens_available: action.payload.tokens,
      },
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // const searchParams = useSearchParams();
  const { setOtpRequest, setOtpSession, startOtpTimer, clearOtpKeys } = useOtpSession();
  const { setRefCode, clearRefKey } = useRefCode();
  // const returnTo = searchParams.get('returnTo');

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken) {
        setSession(accessToken);

        const response = await axios.get(API_ENDPOINTS.auth.me);

        const user = response?.data ?? null;

        dispatch({
          type: Types.INITIAL,
          payload: {
            user: {
              ...user,
            },
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (username: string, password: string) => {
      const data = {
        username,
        password,
      };

      const response = await axios.post(API_ENDPOINTS.auth.login, data);

      const { access_token } = response.data;

      setSession(access_token);
      clearOtpKeys();

      // dispatch({
      //   type: Types.LOGIN,
      //   payload: {
      //     user: {
      //       username: response?.data?.username ?? '',
      //       mobile: response?.data?.mobile ?? '',
      //     },
      //   },
      // });

      window.location.replace(USER_PATH_AFTER_LOGIN);
      // returnTo || USER_PATH_AFTER_LOGIN;
    },
    [clearOtpKeys]
  );

  // REGISTER
  const register = useCallback(
    async (
      username: string,
      password: string,
      ref: string,
      mobile: string,
      otp_session: string
    ) => {
      const data = {
        username,
        password,
        ref,
        mobile,
        otp_session,
      };

      await axios.post(API_ENDPOINTS.auth.register, data);
      clearOtpKeys();
      clearRefKey();
      // const { accessToken, user } = response.data;

      // localStorage.setItem(STORAGE_KEY, accessToken);

      // dispatch({
      //   type: Types.REGISTER,
      //   payload: {
      //     user,
      //   },
      // });
    },
    [clearOtpKeys, clearRefKey]
  );

  // RESET PASSWORD
  const resetPassword = useCallback(
    async (mobile: string, new_password: string, otp_session: string) => {
      await axios.post(API_ENDPOINTS.auth.forgot_password, { mobile, new_password, otp_session });
      clearOtpKeys();
    },
    [clearOtpKeys]
  );

  // REQUEST OTP
  const requestOTP = useCallback(
    async (purpose: string, mobile: string, ref: string | null) => {
      const data = { purpose };
      await axios.post(API_ENDPOINTS.auth.request_otp, { mobile }, { params: data });
      // store mobile in sessionStorage after successful request
      setOtpRequest(`${mobile}`, purpose);
      startOtpTimer(120);

      if (ref) setRefCode(ref);
    },
    [setOtpRequest, startOtpTimer, setRefCode]
  );

  // VERIFY OTP
  const verifyOTP = useCallback(
    async (code: string, mobile: string) => {
      const response = await axios.post(API_ENDPOINTS.auth.verify_otp, { mobile, code });
      setOtpSession(response?.data?.otp_session);
    },
    [setOtpSession]
  );

  // UPDATE USER
  const updateUser = useCallback((user: AuthUserType) => {
    dispatch({
      type: Types.UPDATE,
      payload: {
        user,
      },
    });
  }, []);

  // UPDATE TOKENS
  const updateTokens = useCallback((tokens: number) => {
    dispatch({
      type: Types.UPDATE_TOKENS,
      payload: {
        tokens,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    // await axios.get(API_ENDPOINTS.auth.logout);
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
    window.location.href = '/auth/login';
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      initialize,
      login,
      register,
      resetPassword,
      requestOTP,
      verifyOTP,
      updateUser,
      updateTokens,
      logout,
    }),
    [
      initialize,
      login,
      logout,
      register,
      resetPassword,
      requestOTP,
      verifyOTP,
      updateUser,
      updateTokens,
      state.user,
      status,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
